// trackingViews.ts
import { trackingFetch } from 'services/endpoints/tracking'

type ViewEvent = { post_id: string; ts: number }

const QUEUE: ViewEvent[] = []
const DEDUPE_SHORT = new Map<string, number>() // post_id -> last ts

const DEDUPE_WINDOW_MS = 30_000
const SEEN_WINDOW_MS = 1000 * 60 * 60 * 24 // 24 hours
const FLUSH_INTERVAL_MS = 5_000
const MAX_BATCH = 25

let flushTimer: number | null = null

const STORAGE_KEY = 'post_views_seen_v1'

type SeenMap = Record<string, number> // postId -> lastSeenTs

function loadSeen(): SeenMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as SeenMap
  } catch {
    return {}
  }
}

function saveSeen(seen: SeenMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seen))
  } catch {
    // ignore storage quota errors
  }
}

function hasSeenRecently(postId: string): boolean {
  const seen = loadSeen()
  const last = seen[String(postId)]
  if (!last) return false
  return Date.now() - last < SEEN_WINDOW_MS
}

function markSeen(postId: string) {
  const now = Date.now()
  const cutoff = now - SEEN_WINDOW_MS

  const prev = loadSeen()

  const next: SeenMap = {}

  for (const k of Object.keys(prev)) {
    const ts = prev[k]
    if (typeof ts === 'number' && ts >= cutoff) {
      next[k] = ts
    }
  }

  next[String(postId)] = now

  saveSeen(next)
}

function startFlushTimer() {
  if (flushTimer != null) return
  flushTimer = window.setInterval(() => {
    flush('interval').catch(() => {})
  }, FLUSH_INTERVAL_MS)
}

export function enqueuePostView(postId: string) {
  if (!postId) return

  // long dedupe (don’t even enqueue)
  if (hasSeenRecently(postId)) return

  const now = Date.now()

  // short dedupe (spam protection)
  const last = DEDUPE_SHORT.get(postId)
  if (last && now - last < DEDUPE_WINDOW_MS) return
  DEDUPE_SHORT.set(postId, now)

  QUEUE.push({ post_id: postId, ts: now })

  // mark as seen immediately (so we never re-enqueue on reopen)
  markSeen(postId)

  startFlushTimer()

  if (QUEUE.length >= MAX_BATCH) {
    void flush('max')
  }
}

export async function flush(reason: 'interval' | 'max' | 'pagehide' | 'manual') {
  if (!QUEUE.length) return

  const batch = QUEUE.splice(0, MAX_BATCH)

  // IMPORTANT: use your API base URL + auth headers
  // fire-and-forget; don't await
  trackingFetch('/api/post/views', {
    reason,
    events: batch,
  }).catch(() => {})
}

// Flush when leaving / backgrounding
window.addEventListener('pagehide', () => {
  flush('pagehide').catch(() => {})
})

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') void flush('pagehide')
})
